import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../store';
import { refreshMeAsync } from '../store/authSlice';
import { apiService } from '../services/api';
import {
  OnboardingWizard,
  OnboardingPayload,
} from '../components/onboarding/OnboardingWizard';
import {
  buildDashflowOnboardingConfig,
  ensureOnboardingFontsLoaded,
} from '../config/onboardingConfig';

/**
 * CompletarPerfil — /completar-perfil route.
 *
 * Guarded by PrivateRoute + admin role. Sellers never reach this page (they
 * are onboarded by their admin). On successful completion the user is
 * redirected to /dashboard with a fresh tenant (includes onboardingCompletedAt).
 */
export const CompletarPerfil: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { user, tenant } = useSelector((s: RootState) => s.auth);

  // Pre-load editorial fonts so the wizard renders in Fraunces from the first frame.
  useEffect(() => {
    ensureOnboardingFontsLoaded();
  }, []);

  // If the tenant already completed onboarding, bounce to dashboard — users
  // shouldn't be able to re-run the wizard through the URL.
  useEffect(() => {
    if (tenant?.onboardingCompletedAt) {
      navigate('/dashboard', { replace: true });
    }
  }, [tenant?.onboardingCompletedAt, navigate]);

  const config = useMemo(
    () =>
      buildDashflowOnboardingConfig({
        name: user?.name,
        avatarUrl: user?.profilePictureUrl,
        email: user?.email,
      }),
    [user?.name, user?.profilePictureUrl, user?.email],
  );

  const handleLogoUpload = async (file: File): Promise<string> => {
    const res = await apiService.uploadTenantLogo(file);
    return res.logoUrl;
  };

  const handleComplete = async (payload: OnboardingPayload, uploadedLogoUrl?: string) => {
    await apiService.updateTenantOnboarding({
      ownerName: payload.ownerName,
      ownerBirthday: payload.ownerBirthday || undefined,
      ownerPhone: payload.ownerPhone,
      ownerInstagram: payload.ownerInstagram || undefined,
      ownerWeb: payload.ownerWeb || undefined,
      businessKind: payload.activity,
      businessVolume: payload.volume,
      businessWorkMode: payload.workMode,
      themeCode: payload.themeCode,
      primaryColor: payload.primaryColor,
      secondaryColor: payload.secondaryColor,
      logoUrl: uploadedLogoUrl,
    });

    // Re-hydrate the auth state so onboardingCompletedAt is now set.
    try {
      await dispatch(refreshMeAsync()).unwrap();
    } catch {
      // Non-fatal — the wizard's PATCH already marked it completed server-side.
    }
    navigate('/dashboard', { replace: true });
  };

  return (
    <OnboardingWizard
      config={config}
      onComplete={handleComplete}
      onLogoUpload={handleLogoUpload}
    />
  );
};

export default CompletarPerfil;
